/** @jsx jsx */

import { Fragment } from 'react';
import { upperFirst } from 'lodash';

import { Colors, Grid, Margin, Text } from '@zapier/design-system';
import { jsx, css } from '@emotion/core';

import Markdown from 'app/developer-v3/components/Markdown';
import { getDeepLinkUrl } from 'app/developer-v3/utils';
import { useQueryCheckDoc } from 'app/developer-v3/hooks';

import ErrorBlock from './Error';
import Footer from './Footer';
import Header from './Header';
import ValidatingLoader from '../Validate/ValidatingLoader';

import { ViolationTypes } from 'app/developer-v3/types';
import type { SemVer } from 'app/developer-v3/types/version';

type Props = {
  checkId: string;
  appId: string | number;
  version: SemVer;
  deepLink: string;
  iconType: ViolationTypes;
  checkType: string;
  violationType: string;
};

const Styles = {
  root: css`
    padding: 15px;
  `,
  separator: css`
    color: ${Colors.GrayWarm4};
    background: ${Colors.GrayWarm4};
  `,
};

/**
 * When the user clicks on a validation error/warning/task
 * this component will fetch the documentation for the clicked rule.
 *
 * Renders a loading state while the request is being made, an error state if it
 * fails to succeed.
 * On success, renders the documentation body & title and a link to the external docs
 *
 * @param {object} props
 */
export default function Docs({
  checkId,
  appId,
  version,
  deepLink,
  iconType,
  checkType,
  violationType,
}: Props) {
  const { data: doc, isError, isLoading, refetch } = useQueryCheckDoc({
    checkId,
  });

  const body = doc?.body ?? '';
  const title = doc?.title ?? '';
  const hasData = !isLoading && !isError && !!body && !!title;

  return (
    <article>
      {hasData && (
        <Fragment>
          <Header
            checkType={checkType}
            iconType={iconType}
            link={
              deepLink
                ? deepLink.indexOf('highlight') !== -1
                  ? getDeepLinkUrl(appId, version, deepLink, {
                      type: iconType,
                      message: title,
                    })
                  : getDeepLinkUrl(appId, version, deepLink, {})
                : null
            }
            violationType={violationType}
          />
          <hr css={Styles.separator} />
        </Fragment>
      )}
      <div css={Styles.root}>
        {isLoading ? (
          <ValidatingLoader padding="20px 0" />
        ) : isError ? (
          <ErrorBlock onClick={() => refetch()} />
        ) : (
          <div>
            <Grid alignItems="center" columns="90% 10%" gap={10}>
              <Text type="Body4">{upperFirst(title)}</Text>
            </Grid>
            <Margin margin="15px 0px 0px">
              <Markdown
                codeBlockCss={{ overflow: 'auto' }}
                codeLineCss={{ overflow: 'auto', wordBreak: 'break-all' }}
                source={body}
                spanCss={{ lineHeight: '1.6em' }}
              />
            </Margin>
          </div>
        )}
      </div>
      {hasData && (
        <Fragment>
          <hr css={Styles.separator} />
          <Footer checkId={checkId} />
        </Fragment>
      )}
    </article>
  );
}
